import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ActionSidePanel from 'src/components/design-system/ActionSidePanel';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useConfirm from 'src/hooks/useConfirm';
import useWindowSize from 'src/hooks/useWindowSize';
import { ShareCopySelectionState } from 'src/types/view_shopping_lists/ShareCopyList';
import { ShoppingList } from 'src/types/view_shopping_lists/ShoppingList';
import ShareCopyListRecipientsContent from './ShareCopyListRecipientsContent';

const EMPTY_SELECTION: ShareCopySelectionState = {
  selectedUserIds: [],
  folderAddUsers: [],
  folderRemoveUsers: [],
};

export interface CopyShoppingListSidePanelProps {
  isOpen: boolean;
  shoppingList?: ShoppingList | null;
  folderIds: number[];
  isCopying?: boolean;
  onClose: () => void;
  onCopy: (payload: { folderIds: number[]; folderAddUsers: number[] }) => Promise<void>;
}

const CopyShoppingListSidePanel: FC<CopyShoppingListSidePanelProps> = ({
  isOpen,
  shoppingList = null,
  folderIds,
  isCopying = false,
  onClose,
  onCopy,
}) => {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const { width: viewportWidth } = useWindowSize();
  const [selection, setSelection] = useState<ShareCopySelectionState>(EMPTY_SELECTION);

  useEffect(() => {
    if (!isOpen) {
      setSelection(EMPTY_SELECTION);
    }
  }, [isOpen]);

  const hasSelectionChanges =
    selection.folderAddUsers.length > 0 || selection.folderRemoveUsers.length > 0;

  const confirmDiscardChanges = useCallback(async (): Promise<boolean> => {
    if (!hasSelectionChanges) {
      return true;
    }

    return confirm({
      title: t('Unsaved Changes'),
      content: t('You have unsaved changes. Do you want to discard them?'),
      cancelButtonText: t('Cancel'),
      confirmButtonText: t('Yes, Discard'),
      width: '500px',
    });
  }, [confirm, hasSelectionChanges, t]);

  const handleClose = useCallback(async () => {
    if (isCopying) {
      return;
    }

    const shouldClose = await confirmDiscardChanges();
    if (shouldClose) {
      onClose();
    }
  }, [confirmDiscardChanges, isCopying, onClose]);

  const handleCopy = useCallback(async () => {
    if (folderIds.length === 0) {
      toast.error(t('Please select list(s) to Copy.'));
      return;
    }

    // Pre-selected recipients can receive the copy again, so only require a selection, not a change.
    if (selection.selectedUserIds.length === 0) {
      toast.error(t('Please select at least one recipient.'));
      return;
    }

    try {
      // Copy API accepts currently selected recipients only (no folderRemoveUsers).
      await onCopy({
        folderIds,
        folderAddUsers: selection.selectedUserIds,
      });
    } catch {
      toast.error(t('Failed to copy shopping list'));
    }
  }, [folderIds, onCopy, selection.selectedUserIds, t]);

  const listTitle = shoppingList?.title?.trim();
  const showListNameInTitle = folderIds.length <= 1 && Boolean(listTitle);
  const panelTitle = showListNameInTitle
    ? `${t('Copy List')} - ${listTitle}`
    : t('Copy List');

  const saveDisabled = folderIds.length === 0 || selection.selectedUserIds.length === 0;

  const panelWidth = useMemo(
    () => (viewportWidth >= 768 ? 'min(92vw, 1200px)' : '100%'),
    [viewportWidth]
  );

  const saveDisabledTooltip = (
    <PopUpContentWrapper
      type="Information"
      contents={[t('Please select at least one recipient.')]}
    />
  );

  return (
    <ActionSidePanel
      isOpen={isOpen}
      onClose={handleClose}
      title={panelTitle}
      titleTooltip={listTitle}
      showTitleTooltip={showListNameInTitle}
      onSave={handleCopy}
      onCancel={handleClose}
      saveLabel={t('Send a Copy')}
      saveDisabled={saveDisabled}
      saveLoading={isCopying}
      cancelDisabled={isCopying}
      saveDisabledTooltip={saveDisabledTooltip}
      width={panelWidth}
      className="bg-[#F1F1F1] share-copy-shopping-list-panel"
      contentClassName="min-h-0 overflow-hidden flex flex-col"
    >
      {isOpen ? (
        <ShareCopyListRecipientsContent
          mode="copy"
          folderIds={folderIds}
          onSelectionChange={setSelection}
        />
      ) : null}
    </ActionSidePanel>
  );
};

export default CopyShoppingListSidePanel;
